import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    // Load cart from localStorage on init
    const saved = localStorage.getItem('cartItems');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Ensure all items have a cartItemId for backward compatibility
      return parsed.map(item => ({
        ...item,
        cartItemId: item.cartItemId || `${item._id}-${item.personalization || ''}`
      }));
    }
    return [];
  });

  // Sync to localStorage whenever cart changes
  useEffect(() => {
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product) => {
    const cartItemId = `${product._id}-${product.personalization || ''}`;
    const productWithCartId = { ...product, cartItemId };

    const exists = cartItems.find((item) => item.cartItemId === cartItemId);
    if (exists) {
      setCartItems(
        cartItems.map((item) =>
          item.cartItemId === cartItemId
            ? { ...item, quantity: item.quantity + (product.quantity || 1) }
            : item
        )
      );
    } else {
      setCartItems([...cartItems, { ...productWithCartId, quantity: product.quantity || 1 }]);
    }
  };

  const removeFromCart = (cartItemId) => {
    setCartItems(cartItems.filter((item) => item.cartItemId !== cartItemId));
  };

  const updateQuantity = (cartItemId, newQuantity) => {
    if (newQuantity < 1) return;
    setCartItems(
      cartItems.map((item) =>
        item.cartItemId === cartItemId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
